import { useMemo } from 'react';
import {
  MutationResultPair,
  PaginatedQueryResult,
  queryCache,
  QueryResult,
  useMutation,
  usePaginatedQuery,
  useQuery,
} from 'react-query';
import {
  HiringSite,
  HiringSiteApi,
  HiringSiteApiBatchHiringSitesRequest,
  HiringSitesStates,
  PaginatedHiringSites,
} from 'eve-account-client';
import { useConfiguration } from './BaseApi';
import { removeEmptyStrings } from '../utils/util';

export const useHiringSiteApi = (): HiringSiteApi => {
  const configuration = useConfiguration();
  const api = useMemo(() => new HiringSiteApi(configuration), [configuration]);
  return api;
};

export const getSitesByAccountId = (
  accountId: string,
  page: number,
  size: number,
  state: string,
  api: HiringSiteApi,
): Promise<PaginatedHiringSites> => {
  const request = api.getSitesByAccountId({ accountId, page, size, state });
  return request;
};

const createHiringSite = ({
  accountId,
  hiringSite,
  api,
}: {
  accountId: string;
  hiringSite: HiringSite;
  api: HiringSiteApi;
}): Promise<HiringSite> => {
  removeEmptyStrings(hiringSite);
  return api.addHiringSite({ accountId, hiringSite });
};

const bulkUploadHiringSites = ({
  accountId,
  hiringSites,
  api,
}: {
  accountId: string;
  hiringSites: HiringSite[];
  api: HiringSiteApi;
}): Promise<HiringSite[]> => {
  hiringSites.forEach((site) => {
    removeEmptyStrings(site);
  });
  return api.batchHiringSites({ accountId, hiringSite: hiringSites } as HiringSiteApiBatchHiringSitesRequest);
};

const updateHiringSite = ({
  accountId,
  hiringSiteId,
  hiringSite,
  api,
}: {
  accountId: string;
  hiringSiteId: string;
  hiringSite: HiringSite;
  api: HiringSiteApi;
}): Promise<HiringSite> => {
  removeEmptyStrings(hiringSite);
  return api.updateHiringSite({ accountId, id: hiringSiteId, hiringSite });
};

const deleteHiringSite = ({
  accountId,
  hiringSiteId,
  api,
}: {
  accountId: string;
  hiringSiteId: string;
  api: HiringSiteApi;
}): Promise<void> => {
  const request = api.deleteHiringSite({ accountId, id: hiringSiteId });
  return request;
};

const syncHiringSite = ({ accountId, api }: { accountId: string; api: HiringSiteApi }): Promise<void> => {
  const request = api.syncHiringSites({ accountId });
  return request;
};

export const useHiringSitesCountQuery = (
  accountId?: string,
): QueryResult<[number | undefined, number, HiringSitesStates[]], Error | Response> => {
  const api = useHiringSiteApi();
  // Query limited dataset as we only need the totalElements value from the query.
  const page = 0;
  const size = 1;

  return useQuery<[number | undefined, number, HiringSitesStates[]], Error | Response>(
    ['hiringSitesCount', accountId],
    async () => {
      const response = await getSitesByAccountId(accountId as string, page, size, '', api);
      const legacyCount = response.states.reduce((total, { count }) => total + count, 0);
      return [response.paginated?.totalElements, legacyCount, response.states];
    },
    {
      enabled: Boolean(accountId),
    },
  );
};

export const useHiringSitesPaginatedQuery = (
  accountId?: string,
  page = 0,
  size = 1,
  state = '',
): PaginatedQueryResult<PaginatedHiringSites, Error | Response> => {
  const api = useHiringSiteApi();

  return usePaginatedQuery(
    ['paginatedHiringSites', accountId, { page, size, state }],
    () => getSitesByAccountId(accountId as string, page, size, state, api),
    {
      enabled: Boolean(accountId),
    },
  );
};

export const useHiringSitesCreateMutation = (
  onError: (error: Error | Response) => void,
): MutationResultPair<HiringSite, Error | Response, { accountId: string; hiringSite: HiringSite }, unknown> => {
  const api = useHiringSiteApi();

  return useMutation<HiringSite, Error | Response, { accountId: string; hiringSite: HiringSite }>(
    ({ accountId, hiringSite }) =>
      createHiringSite({
        accountId,
        hiringSite,
        api,
      }),
    {
      onSuccess: (hiringSite, { accountId }) => {
        queryCache.setQueryData<HiringSite>(['hiringSite', hiringSite.id], hiringSite);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['paginatedHiringSites', accountId]);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['hiringSitesCount', accountId]);
      },
      onError: (error) => {
        onError(error);
      },
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};

export const useHiringSitesBulkUploadMutation = (
  onError: (error: Error | Response) => void,
): MutationResultPair<HiringSite[], Error | Response, { accountId: string; hiringSites: HiringSite[] }, unknown> => {
  const api = useHiringSiteApi();

  return useMutation<HiringSite[], Error | Response, { accountId: string; hiringSites: HiringSite[] }>(
    ({ accountId, hiringSites }) =>
      bulkUploadHiringSites({
        accountId,
        hiringSites,
        api,
      }),
    {
      onSuccess: (hiringSites, { accountId }) => {
        hiringSites.forEach((site) => {
          queryCache.setQueryData<HiringSite>(['hiringSite', site.id], site);
        });
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['paginatedHiringSites', accountId]);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['hiringSitesCount', accountId]);
      },
      onError: (error) => {
        onError(error);
      },
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};

export const useHiringSitesUpdateMutation = (
  onError: (error: Error | Response) => void,
): MutationResultPair<
  HiringSite,
  Error | Response,
  { accountId: string; hiringSiteId: string; hiringSite: HiringSite },
  unknown
> => {
  const api = useHiringSiteApi();

  return useMutation<
    HiringSite,
    Error | Response,
    { accountId: string; hiringSiteId: string; hiringSite: HiringSite },
    unknown
  >(
    ({ accountId, hiringSiteId, hiringSite }) =>
      updateHiringSite({
        accountId,
        hiringSiteId,
        hiringSite,
        api,
      }),
    {
      onSuccess: (hiringSite, { accountId }) => {
        queryCache.setQueryData<HiringSite>(['hiringSite', hiringSite.id], hiringSite);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['paginatedHiringSites', accountId]);
      },
      onError: (error) => {
        onError(error);
      },
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};

export const useHiringSitesDeleteMutation = (): MutationResultPair<
  void,
  Error | Response,
  { accountId: string; hiringSiteId: string },
  unknown
> => {
  const api = useHiringSiteApi();

  return useMutation<void, Error | Response, { accountId: string; hiringSiteId: string }>(
    ({ accountId, hiringSiteId }) =>
      deleteHiringSite({
        accountId,
        hiringSiteId,
        api,
      }),
    {
      onSuccess: (_, { accountId }) => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['paginatedHiringSites', accountId]);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['hiringSitesCount', accountId]);
      },
    },
  );
};

export const useHiringSitesSyncMutation = (): MutationResultPair<
  void,
  Error | Response,
  { accountId: string },
  unknown
> => {
  const api = useHiringSiteApi();

  return useMutation<void, Error | Response, { accountId: string }>(
    ({ accountId }) =>
      syncHiringSite({
        accountId,
        api,
      }),
    {
      onSuccess: (_, { accountId }) => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['paginatedHiringSites', accountId]);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['hiringSitesCount', accountId]);
      },
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};
